import { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import { Check } from 'lucide-react';
// No need to import CSS here - it's imported globally in main.tsx

interface PhoneInputFieldProps {
  value: string;
  countryCode: string;
  onChange: (phone: string, countryCode: string) => void;
  error?: string;
  disabled?: boolean;
  onEnterKey?: () => void;
  nextFieldId?: string;
  id?: string;
  name?: string;
}

export function PhoneInputField({
  value,
  countryCode,
  onChange,
  error,
  disabled = false,
  onEnterKey,
  nextFieldId,
  id = 'phone',
  name = 'phone'
}: PhoneInputFieldProps) {
  // Local state for the phone input value
  const [phoneInputValue, setPhoneInputValue] = useState<string>('');
  
  // Initialize from props
  useEffect(() => {
    if (countryCode) {
      const cleanCountryCode = countryCode.startsWith('+') 
        ? countryCode.substring(1) 
        : countryCode;
      
      setPhoneInputValue(cleanCountryCode + (value || ''));
    }
  }, [countryCode, value]);
  
  // Handle phone number change
  const handlePhoneChange = (value: string, data: { dialCode: string; countryCode: string }) => {
    setPhoneInputValue(value);
    
    let phoneNumber = value;
    if (value.startsWith(data.dialCode)) {
      phoneNumber = value.substring(data.dialCode.length);
    }
    
    onChange(phoneNumber, `+${data.dialCode}`);
  };
  
  return (
    <div className={`enhanced-phone-input ${
      error ? 'phone-input-error' : value ? 'phone-input-valid' : ''
    }`}>
      <PhoneInput
        country={'us'}
        value={phoneInputValue}
        onChange={handlePhoneChange}
        disabled={disabled}
        inputProps={{
          id: id,
          name: name,
          autoFocus: false,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (nextFieldId) {
                const nextField = document.getElementById(nextFieldId);
                if (nextField) nextField.focus();
              } else if (onEnterKey) {
                onEnterKey();
              }
            }
          }
        }}
        countryCodeEditable={false}
        enableSearch={true}
        searchPlaceholder="Search countries..."
        placeholder="Enter phone number"
        autoFormat={false}
        preferredCountries={['us', 'gb', 'ca', 'es', 'fr', 'de', 'it']}
      />
      
      {value && !error && (
        <div className="valid-indicator">
          <Check className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}